// Modules
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"

// Constants
import { ERROR_MESSAGES } from "~constants/errorMessages"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import {
  GetAssignableRoles,
  GetHighestRoles,
  GetRoleChildren,
  ROLES_USER_FACING,
} from "~rbac/constants/rbac-roles"
import IsAuthorized from "~rbac/isAuthorized"
import isCompanyRole from "~rbac/utils/isCompanyRole"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Context
import { useAuthContext } from "~context/authContext"

// Utils
import { Validation } from "~utils/regexValidations"
import { createDropdownCollection, createDropdownValue } from "~utils/dropdown"
import { generatePassword } from "~utils/passwordGenerator"

// Components
import Input from "~app/Input"
import Modal from "~app/Modal"
import ControlSelect from "~app/ControlSelect"
import { ErrorMessageSmall } from "~styled/errors"
import { WizardColumn, WizardRow } from "~styled/wizard"
import CopyCell from "~components/CopyCell"
import { ButtonPrimary } from "~styled/button"
import { CircularProgress } from "@mui/material"
import {
  WebInviteContainer,
  WebInviteCheckbox,
  WebInviteLabel,
} from "./NewUserModal.styled"

// Services
import UserService from "~services/UserService"
import RoleService from "~services/RoleService"
import CompanyService from "~services/CompanyService"

// Interfaces
import { RolesShape } from "~shapes/role"
import { SelectOption } from "~utils/dropdown"
import { CompanyShape } from "~shapes/company"
import { UserShape, PostUserShape } from "~shapes/user"

interface UpdateUserPayload extends Omit<PostUserShape, "roles"> {
  roles?: string[]
}

interface NewUserModalProps {
  user?: UserShape
  isGettingUser: boolean
  onRequestClose: (shouldRefreshList?: boolean) => void
  onUserUpdated: (updatedUser: UserShape) => void
}

interface NewUserFormData {
  email: string
  name: string
  company?: SelectOption
  roles?: SelectOption[]
  registrationCompleted: boolean
  isWebInvited: boolean
  firstName?: string
  lastName?: string
  password?: string
}

const companyRequestParams = { sort: "name", size: 100 }

const NewUserModal = ({
  user,
  isGettingUser,
  onRequestClose,
  onUserUpdated,
}: NewUserModalProps) => {
  const {
    formState: { errors },
    control,
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<NewUserFormData>({
    defaultValues: {
      registrationCompleted: true,
      isWebInvited: true,
    },
  })

  // Get from context
  const { loggedCompany } = useAuthContext()

  const [roles, setRoles] = useState<RolesShape[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [companies, setCompanies] = useState<CompanyShape[]>([])
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [canUpdateRoles, setCanUpdateRoles] = useState<boolean>(false)
  const [canUpdateCompany, setCanUpdateCompany] = useState<boolean>(false)

  const email = watch("email")

  // Effect to check user authorizations
  useEffect(() => {
    const canUpdateRoles = checkAuthorization(RBAC_RULES.UPDATE_USER_ROLES)
    const canUpdateCompany = checkAuthorization(RBAC_RULES.UPDATE_USER_COMPANY)

    setCanUpdateRoles(canUpdateRoles)
    setCanUpdateCompany(canUpdateCompany)
  }, [])

  // Effect to fetch companies and roles
  useEffect(() => {
    ;(async () => {
      setIsLoading(true)

      // Fetch Roles
      if (canUpdateRoles) {
        await fetchRoles()
      }

      // Fetch Companies
      if (canUpdateCompany) {
        await fetchCompanies()
      }

      setIsLoading(false)
    })()
  }, [canUpdateRoles, canUpdateCompany])

  // Effect to fill the form with user data
  useEffect(() => {
    if (user) {
      let company: SelectOption = null!
      let userRole: SelectOption[] = null!

      if (user?.company) {
        company = createDropdownValue(
          user.company.name,
          user.company._links?.self.href || "",
        )
      }

      if (user?.roles) {
        const roles = GetHighestRoles(user.roles)
        userRole = roles
          .filter(
            (obj, index, array) =>
              index === array.findIndex(o => o.id === obj.id),
          )
          .map(role => {
            return createDropdownValue(
              ROLES_USER_FACING[role.name] || role.name,
              role.name || "",
            )
          })
      }

      reset({
        email: user.email,
        roles: userRole,
        company,
        name:
          user.profile?.name?.trim() ||
          `${user.profile?.firstName || ""} ${
            user.profile?.lastName || ""
          }`.trim(),
        registrationCompleted: user.registrationCompleted,
        isWebInvited: true,
      })
    }
  }, [user])

  const fetchCompanies = async () => {
    try {
      const companies = await CompanyService.getCompanies(companyRequestParams)
      setCompanies(companies._embedded.companies)
    } catch (err) {
      setErrorMessage(ERROR_MESSAGES.FETCHING_COMPANIES)
    }
  }

  const fetchRoles = async () => {
    try {
      const roles = await RoleService.getRoles()
      setRoles(roles._embedded.roles)
    } catch (err) {
      setErrorMessage(ERROR_MESSAGES.FETCHING_ROLES)
    }
  }

  const createCompaniesDropdown = (): SelectOption[] => {
    const parsedCompanies = companies.map(c => ({ name: c.name, id: c.id }))
    return createDropdownCollection(parsedCompanies, "name", "id")
  }

  const createRolesDropdown = (): SelectOption[] => {
    const assignableRoles = GetAssignableRoles(roles)
    const parsedRoles = assignableRoles.map(r => ({
      label: ROLES_USER_FACING[r.name] || r.name,
      name: r.name,
    }))
    return createDropdownCollection(parsedRoles, "label", "name")
  }

  const handleSave = async formData => {
    setIsLoading(true)

    const userPayload: PostUserShape = {
      email: formData.email,
      profile: {
        name: `${formData.name}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
      },
      registrationCompleted: formData.registrationCompleted,
    }

    // Only add these fields when creating a new user
    if (!user) {
      userPayload.registrationCompleted = formData.registrationCompleted
      userPayload.isWebInvited = formData.isWebInvited
    }

    // Only add password if it's defined and not empty
    if (formData.password?.trim()) {
      userPayload.password = formData.password
    }

    // Pick selected company
    let selectedCompany: null | CompanyShape = null
    if (formData.company) {
      selectedCompany =
        companies.find(el => el.id === formData.company.value) || null
    }

    // Add selected company if we can and the field is required
    if (canUpdateCompany && selectedCompany) {
      userPayload.company = selectedCompany?._links?.self.href
    }

    const selectedRoleValues = formData.roles?.map(r => r.value) || []
    const allRoles = canUpdateRoles
      ? GetRoleChildren(selectedRoleValues, roles).filter(Boolean)
      : []

    if (user) {
      // For update: always patch existing user, optionally include roles
      const { roles: _userRoles, ...userPayloadWithoutRoles } = userPayload
      const updatePayload: UpdateUserPayload = {
        ...userPayloadWithoutRoles,
        id: user.id,
      }

      if (canUpdateRoles) {
        updatePayload.roles = allRoles.map(r => r._links.self.href)
      }

      try {
        const updatedUser = await UserService.patchUser(updatePayload)
        onUserUpdated(updatedUser)
        setErrorMessage("")
        onRequestClose()
      } catch (err) {
        setErrorMessage(err.message)
        setIsLoading(false)
      }
      return
    }

    // For create: use role names when role editing is allowed
    if (canUpdateRoles) {
      userPayload.roles = allRoles.map(r => ({ name: r.name }))
    }

    // If we are creating a new user and can't update company
    if (!canUpdateCompany && isCompanyRole()) {
      userPayload.company = loggedCompany?._links?.self.href || ""
    }

    try {
      await UserService.postUser(userPayload)
      setErrorMessage("")
      onRequestClose(true)
    } catch (err) {
      setErrorMessage(err.message)
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      buttonLabel={user ? "Update" : "Create"}
      cancelLabel="Cancel"
      cancelButton={!isLoading && !isGettingUser}
      isButtonLoading={isLoading || isGettingUser}
      onRequestClose={() => onRequestClose(false)}
      onRequestConfirm={handleSubmit(handleSave)}
    >
      {isGettingUser ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          <WizardRow>
            <IsAuthorized action={RBAC_RULES.UPDATE_USER_COMPANY}>
              <WizardColumn>
                <ControlSelect
                  name={"company"}
                  label={"Company"}
                  rules={{ required: false }}
                  height={200}
                  control={control as any}
                  options={createCompaniesDropdown()}
                  disabled={isLoading || isGettingUser}
                  isLoading={isLoading}
                  errorMessage={errors.company && ERROR_MESSAGES.FIELD_REQUIRED}
                />
              </WizardColumn>
            </IsAuthorized>

            <IsAuthorized action={RBAC_RULES.UPDATE_USER_ROLES}>
              <WizardColumn>
                <ControlSelect
                  name={"roles"}
                  label={"Role(s)"}
                  rules={{ required: true }}
                  height={200}
                  control={control as any}
                  isMulti={true}
                  options={createRolesDropdown()}
                  disabled={isLoading || isGettingUser}
                  isLoading={isLoading}
                  errorMessage={errors.roles && ERROR_MESSAGES.FIELD_REQUIRED}
                />
              </WizardColumn>
            </IsAuthorized>
          </WizardRow>

          <WizardRow>
            <WizardColumn>
              <Input
                name="name"
                label="Name"
                register={register("name", { required: true })}
                errorMessage={errors.name && ERROR_MESSAGES.FIELD_REQUIRED}
              />
            </WizardColumn>
          </WizardRow>

          <WizardRow>
            <WizardColumn>
              <Input
                name="email"
                label="Email"
                disabled={!!user}
                register={register("email", {
                  required: true,
                  pattern: {
                    value: Validation.email,
                    message: ERROR_MESSAGES.INVALID_EMAIL,
                  },
                })}
                errorMessage={
                  errors.email &&
                  (errors.email?.message || ERROR_MESSAGES.FIELD_REQUIRED)
                }
              />
            </WizardColumn>
          </WizardRow>

          {user && (
            <WizardRow>
              <WizardColumn>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      name="password"
                      label="Password"
                      register={register("password", {
                        required: false,
                        pattern: {
                          value: Validation.password,
                          message: ERROR_MESSAGES.INVALID_PASSWORD,
                        },
                      })}
                      errorMessage={
                        errors.password &&
                        (errors.password?.message ||
                          ERROR_MESSAGES.FIELD_REQUIRED)
                      }
                    />
                  </div>
                  <div style={{ marginTop: "24px" }}>
                    <ButtonPrimary
                      onClick={() => {
                        const newPassword = generatePassword()
                        reset({ ...watch(), password: newPassword })
                      }}
                      style={{
                        fontSize: "12px",
                        height: "30px",
                        lineHeight: "30px",
                        padding: "0 10px",
                      }}
                    >
                      Create New Password
                    </ButtonPrimary>
                  </div>
                  {email && watch("password") && (
                    <div style={{ marginTop: "24px" }}>
                      <CopyCell
                        rowData={`/new-password?e=${encodeURIComponent(
                          email,
                        )}&p=${encodeURIComponent(watch("password") || "")}`}
                        text={"Copy"}
                        classname="green"
                      />
                    </div>
                  )}
                </div>
              </WizardColumn>
            </WizardRow>
          )}

          {!user && (
            <WizardRow>
              <WizardColumn>
                <WebInviteContainer>
                  <WebInviteCheckbox
                    id="isWebInvited"
                    {...register("isWebInvited")}
                    defaultChecked={true}
                  />
                  <WebInviteLabel htmlFor="isWebInvited">
                    Web invite
                  </WebInviteLabel>
                </WebInviteContainer>
              </WizardColumn>
            </WizardRow>
          )}

          <WizardRow>
            <WizardColumn>
              <WebInviteContainer>
                <WebInviteCheckbox
                  id="registrationCompleted"
                  {...register("registrationCompleted")}
                  defaultChecked={user ? user.registrationCompleted : true}
                />
                <WebInviteLabel htmlFor="registrationCompleted">
                  Registration completed
                </WebInviteLabel>
              </WebInviteContainer>
            </WizardColumn>
          </WizardRow>

          <ErrorMessageSmall style={{ textAlign: "right" }}>
            {errorMessage}
          </ErrorMessageSmall>
        </>
      )}
    </Modal>
  )
}

export default NewUserModal
